import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'

import type { StatusOption } from './StatusFilterBar'

/** Рядок довідника статусів так, як його віддає `GET /services/<…>_statuses`. */
export interface StatusDictionaryItem {
    code?: string | null
    title?: string | null
    color?: string | null
    sorting?: number | null
}

interface CollectionResponse {
    items?: StatusDictionaryItem[]
    'hydra:member'?: StatusDictionaryItem[]
}

/**
 * Скільки рядків тягнемо за раз. Довідник статусів закритий і короткий
 * (чотири системні рядки й кілька власних), тож одна сторінка з запасом.
 */
const DICTIONARY_LIMIT = 100

/**
 * Рядки довідника → варіанти рядка статусів: значення — код (за ним фільтрує
 * список і рахує лічильники бек), підпис і колір — з довідника, порядок — за
 * `sorting`, а за однакового — за назвою, щоб він не стрибав між завантаженнями.
 *
 * Рядок без коду пропускаємо: фільтрувати за ним нічим, а плашка без числа
 * виглядала б як зламана.
 */
export const toStatusOptions = (items: readonly StatusDictionaryItem[]): StatusOption[] =>
    [...items]
        .filter((item): item is StatusDictionaryItem & { code: string } => typeof item.code === 'string' && item.code !== '')
        .sort(
            (a, b) =>
                (a.sorting ?? 500) - (b.sorting ?? 500) || String(a.title ?? '').localeCompare(String(b.title ?? ''), 'uk')
        )
        .map((item) => ({
            value: item.code,
            label: typeof item.title === 'string' && item.title !== '' ? item.title : item.code,
            ...(typeof item.color === 'string' && item.color !== '' ? { color: item.color } : {}),
        }))

/**
 * Варіанти статусу з довідника (`SPEC-007` §5.1): «Статуси клієнтів»,
 * «Статуси перевізників», «Статуси справ».
 *
 * У співробітника статус — enum, і його варіанти лежать у ресурсі
 * (`options`). У CRM-сутностей статус — рядок довідника: назву й колір
 * змінює адміністратор, тож вписати їх у ресурс не можна — вони розійдуться з
 * довідником після першої ж правки.
 *
 * Один екземпляр на довідник (`statusDictionary(key)`): рядок статусів над
 * таблицею і поле «Статус» панелі фільтрів читають той самий перелік.
 *
 * ⚠️ Перечитуємо на кожне відкриття реєстру (`refresh`), але старий перелік
 * лишається на екрані, доки не прийде новий: інакше плашки на мить зникали б,
 * а адміністратор, який щойно перефарбував статус, бачив би старий колір до
 * перезавантаження сторінки.
 */
export class StatusDictionaryStore {
    options: StatusOption[] = []

    /** Перелік прийшов хоча б раз: до цього плашок статусів не малюємо. */
    isLoaded = false

    private controller: AbortController | null = null

    constructor(private readonly resourceKey: string) {
        makeAutoObservable<StatusDictionaryStore, 'controller' | 'resourceKey'>(this, {
            controller: false,
            resourceKey: false,
        })
    }

    async refresh(): Promise<void> {
        const restUrl = GetResourceTask(this.resourceKey)?.restUrl

        if (typeof restUrl !== 'string' || restUrl === '') return

        this.controller?.abort()

        const controller = new AbortController()
        this.controller = controller

        try {
            // `signal` вимикає й 30-секундний кеш GET ядрового клієнта: свіжий
            // колір статусу має з'явитися з наступним відкриттям реєстру
            const response = (await restClient.get(restUrl, {
                searchParams: { limit: DICTIONARY_LIMIT },
                signal: controller.signal,
            })) as unknown as CollectionResponse | StatusDictionaryItem[] | null

            if (controller.signal.aborted) return

            const items = Array.isArray(response) ? response : (response?.items ?? response?.['hydra:member'] ?? [])

            runInAction(() => {
                this.options = toStatusOptions(items)
                this.isLoaded = true
            })
        } catch (error: unknown) {
            // Скасований запит замінив свіжіший. Решту ковтаємо: рядок статусів —
            // підказка над таблицею, і таблиця не повинна падати разом із ним
            if ((error as Error)?.name === 'AbortError') return
        }
    }
}

const stores = new Map<string, StatusDictionaryStore>()

/** Спільний стор довідника за ключем його ресурсу (`Setting.ListClientStatus`). */
export const statusDictionary = (resourceKey: string): StatusDictionaryStore => {
    let store = stores.get(resourceKey)

    if (!store) {
        store = new StatusDictionaryStore(resourceKey)
        stores.set(resourceKey, store)
    }

    return store
}
